import { detectMob, datasetOfDatasetQuery } from '../assets/utils.js'
import frel from '../assets/frel.js'
import Tabs from '../assets/tabs.js'

const tabContainer = '[data-tab-container]'
const tabs = '[data-reveal-tab]'
const activeClass = 'active'
const targetContainer = '[data-tab-content-container]'
const targets = '[data-card-group]'
const targetActiveClass = 'active'

const escapeAttribute = (value = '') => String(value).replace(/[&<>"']/g, character => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
}[character]))

const sliderConfig = {
    slidesPerView: 1.72,
    spaceBetween: 10,
    navigation: {
        nextEl: '[data--rewards-swiper-navigation-right]',
        prevEl: '[data-rewards-swiper-navigation-left]',
    }, pagination: {
        el: '[data-rewards-swiper-pagination]',
        clickable: true,
        type: 'custom',
        renderCustom(_, current, total) {
            return (`
                      ${current}/${total}
                  `)
        }
    },
    breakpoints: {
        750: {
            slidesPerView: 2,
            spaceBetween: 20
        },
        1000: {
            slidesPerView: 4,
            spaceBetween: 20
        }
    }
}

const generateProductImage = (src, alt = 'Reward product') => {
    const img = frel('img', {
        class: 'rewards-redeem_product-image',
        loading: 'lazy',
        decoding: 'async',
        alt
    })
    img.src = src
    return img
}

const generateDetailsOpener = (i) => {
    return frel('button', { class: 'rewards-redeem_product-description reward-toggle-show' + i, onclick: 'toggleDetails(true, ' + i + ');' }, 'Details')
}

const generateDetailsCloser = (i) => {
    return frel('button', { class: 'rewards-redeem_product-closer reward-toggle-hide' + i, onclick: 'toggleDetails(false, ' + i + ');' }, 'Close details')
}

const generateDetailsBody = (description, i) => {
    return frel('p', { class: 'rewards-redeem_product-body reward-toggle-body' + i }, description)
}

const generateProductTitle = (name) => {
    return frel('p', { class: 'rewards-redeem_product-title' }, name)
}

const generateProductPointCost = (costText) => {
    return frel('p', { class: 'rewards-redeem_product-point-cost' }, costText)
}

const generateLoginButton = () => {
    const anchorElement = frel('a', { class: 'rewards-redeem_product-login-button' }, 'login')
    anchorElement.href = '/account/login'
    return anchorElement
}

const generateRedeemButton = (variantId) => {
    const anchorElement = frel('a', { class: 'rewards-redeem_product-redeem-button swell-buy-product-btn' }, 'add to bag')
    anchorElement.dataset.variantId = variantId
    anchorElement.dataset.errorContent = 'Looks like you need more points to redeem this item. Points needed: *|amount|* pts<br>You have: *|point_balance|* pts'
    anchorElement.dataset.successContent = 'Item added to bag.'
    anchorElement.dataset.errorTitle = 'Sorry'
    anchorElement.dataset.successTitle = 'Hooray!'
    anchorElement.dataset.cancelBtnText = 'NEVER MIND'
    anchorElement.dataset.confirmContent = 'Just checking! You currently have *|point_balance|* points available. This product costs *|amount|* points to redeem.'
    return anchorElement
}

const generateProductCardElement = () => {
    return frel('div', { class: 'rewards-redeem_product-card swiper-slide' })
}

const generateProductCardInnerWrapper = () => {
    return frel('div', { class: 'rewards-redeem_product-card-inner-wrapper' })
}

const loggedInCheck = () => {
    const value = datasetOfDatasetQuery('logged-in-state')
    return value === 'true'
}

// getCart function moved to global.js - using window.getCart() instead

// Main class
class RewardsRedeem extends HTMLElement {
    constructor() {
        super()
        this.productCards = []
        this.swiperInstance = null
        this.tabsInstance = null
        this._initialized = false
        this._initializing = false
        this._listenersAttached = false
        this._existingApiTimer = null
        this._gridInitTimer = null
        this._restoreInitTimer = null
        this._onResize = this.handleResize.bind(this)
        this._onSwellSetup = this.handleSwellSetup.bind(this)
        this._onBeforeProductRedemption = null
    }

    query(selector) {
        return this.querySelector(selector)
    }

    inject(selector, element) {
        const container = this.query(selector)
        if (container) {
            container.appendChild(element)
        }
    }



    filterDisplayByGroup(group) {
        if (group === 'fiveThousand') {
            // Replace with grid layout for 5000 points
            const fiveThousandProducts = this.redemptionProductsSorted?.fiveThousand || []
            this.showGridLayout(fiveThousandProducts)
            return
        }
        
        // Restore carousel if coming from 5000 tab
        this.restoreCarouselLayout()
        
        this.productCards.forEach((productCard) => {
            const thisCardGroup = productCard.dataset?.cardGroup
            const shouldShow = thisCardGroup === group;
            
            if (shouldShow) {
                productCard.style.display = 'flex'
            } else {
                productCard.style.display = 'none'
            }
        });
    }

    showGridLayout(products) {
        const container = this.query('[data-rewards-redeem-slider]')
        if (!container) return

        // Save original carousel content and classes
        if (!this.originalCarouselContent) {
            this.originalCarouselContent = container.innerHTML
            this.originalCarouselClasses = container.className
        }

        // Grid CSS is now included in the main carousel CSS file

        // Create grid HTML structure - exact copy from rewards-redeem-grid
        let gridHTML = ''
        
        if (products.length === 0) {
            gridHTML = `
                <div class="rewards-redeem-empty">
                    <p class="rewards-redeem-empty-header">There are no rewards for this point level at this time.</p>
                    <p class="rewards-redeem-empty-text">We have new rewards every month so make sure to check back soon.</p>
                </div>
            `
        } else {
            products.forEach((product, index) => {
                const { name, appliesToId, costText, description, backgroundImageUrl } = product || {}
                const isReverse = index % 2 === 1
                
                // Get eyebrow text from metaobject mapping, fallback to costText
                const eyebrowText = (window.rewardsEyebrowMapping && window.rewardsEyebrowMapping[appliesToId]) || costText;
                
                gridHTML += `
                    <div class="Rewards-redeem-grid-content${isReverse ? ' reverse' : ''}">
                        <img class="Rewards-redeem-grid-content--image" loading="lazy" decoding="async" alt="${escapeAttribute(name || 'Reward product')}" src="${backgroundImageUrl || ''}" width="auto" height="auto">
                        <div class="Rewards-redeem-grid-content--text">
                            ${eyebrowText ? `<p class="Rewards-redeem-grid-content--text-eyebrow">${eyebrowText}</p>` : ''}
                            ${name ? `<p class="Rewards-redeem-grid-content--text-heading">${name}</p>` : ''}
                            ${description ? `<div class="Rewards-redeem-grid-content--text-text">${description}</div>` : ''}
                            ${costText ? `<p class="Rewards-redeem-grid-content--text-points">${costText}</p>` : ''}
                            ${appliesToId ? `
                                <a href="#" class="Rewards-redeem-grid-content--text-link swell-buy-product-btn" 
                                   data-variant-id="${appliesToId}"
                                   data-error-content="Looks like you need more points to redeem this item. Points needed: *|amount|* pts<br>You have: *|point_balance|* pts"
                                   data-success-content="Item added to bag."
                                   data-error-title="Sorry"
                                   data-success-title="Hooray!"
                                   data-cancel-btn-text="NEVER MIND"
                                   data-confirm-content="Just checking! You currently have *|point_balance|* points available. This product costs *|amount|* points to redeem."
                                   style="cursor: pointer;">
                                   add to bag
                                </a>
                            ` : ''}
                        </div>
                    </div>
                `
            })
        }
        
        // Replace carousel with grid - remove all carousel classes
        container.innerHTML = gridHTML
        container.className = 'Rewards-redeem-grid'
        
        // Re-initialize Swell buttons with a small delay to ensure DOM is ready
        this._gridInitTimer = setTimeout(() => {
            this._gridInitTimer = null
            if (!this.isConnected) return
            const swellPointsApi = window.spapi
            if (swellPointsApi && typeof swellPointsApi.setupBuyProductWithPointsHelper === 'function') {
                swellPointsApi.setupBuyProductWithPointsHelper()
            }
            if (window.swellAPI && typeof window.swellAPI.refreshEmbeds === 'function') {
                window.swellAPI.refreshEmbeds()
            }
            
            // Also manually attach click event handlers as fallback
            const buttons = container.querySelectorAll('.swell-buy-product-btn')
            buttons.forEach(button => {
                button.addEventListener('click', function(e) {
                    e.preventDefault()
                    // Trigger Swell's redemption flow
                    if (window.spapi && typeof window.spapi.buyProductWithPoints === 'function') {
                        const variantId = this.getAttribute('data-variant-id')
                        if (variantId) {
                            window.spapi.buyProductWithPoints(variantId)
                        }
                    }
                })
            })
        }, 100)
    }

    restoreCarouselLayout() {
        const container = this.query('[data-rewards-redeem-slider]')
        if (!container || !this.originalCarouselContent) return

        container.innerHTML = this.originalCarouselContent
        container.className = this.originalCarouselClasses || 'rewards-redeem_tab-slide-container swiper'
        
        // Repopulate the productCards array with the restored DOM elements
        this.productCards = Array.from(this.querySelectorAll('.rewards-redeem_product-card'))
        
        // Reinitialize Swell functionality for the restored buttons
        this._restoreInitTimer = setTimeout(() => {
            this._restoreInitTimer = null
            if (!this.isConnected) return
            this.initProductCards()
        }, 100)
    }

    initProductCards() {
        if (window.spapi && typeof window.spapi.setupBuyProductWithPointsHelper === 'function') {
            window.spapi.setupBuyProductWithPointsHelper()
        }
        if (window.swellAPI && typeof window.swellAPI.refreshEmbeds === 'function') {
            window.swellAPI.refreshEmbeds()
        }
    }

    initRedeemSucess() {
        if (this._onBeforeProductRedemption) return
        this._onBeforeProductRedemption = async (opts, redemptionOption, pointBalance, cb) => {
            if (typeof window.getCart !== 'function') {
                cb()
                return
            }
            const cart = await window.getCart()
            if (cart.items_subtotal_price < 1) {
                if (!window.spapi?.$?.confirm) {
                    cb()
                    return
                }
                window.spapi.$.confirm({
                    title: "Oops!",
                  content: "You can only redeem points for a free product when making a purchase.",
                  type: "red",
                  typeAnimated: true,
                  useBootstrap: false,
                  boxWidth: "400px",
                  buttons: {
                    ok: {
                      text: "Ok",
                      action: function () {
                      }
                    }
                  }
                });
            } else {
                cb()
            }
        }
        window.onBeforeProductRedemption = this._onBeforeProductRedemption
    }

    initTabButtons() {
        if (this.tabsInstance) return
        this.tabsInstance = new Tabs({
            tabContainer,
            tabs,
            activeClass,
            targetContainer,
            targets,
            targetActiveClass,
            funcSettings: {
                beforeTargetClassAdded: (_, tab) => {
                    const group = tab.dataset?.targetGroup
                    const sliderControls = this.query('[data-rewards-swiper-pagination]')
                    this.destroySlider()
                    this.filterDisplayByGroup(group)
                    // Only initialize slider if we're not in grid mode
                    const sliderElement = this.query('[data-rewards-redeem-slider]')
                    if (sliderElement && sliderElement.classList.contains('swiper')) {
                        this.initSlider()
                    }
                    
                    if (sliderControls) {
                        sliderControls.style.display = 'none'
                        const visibleProductCards = this.querySelectorAll('[data-card-group]:not([style*="display: none"])')
                        if (detectMob() && visibleProductCards.length > 1) {
                            sliderControls.style.display = 'flex'
                        } else if (visibleProductCards.length > 4) {
                            sliderControls.style.display = 'flex'
                        }
                    }
                    // return true to stop the target class from being added
                    return true
                }
            }
        })
    }

    renderProductCard(data, group, i) {
        const { name, appliesToId, costText, description, backgroundImageUrl } = data || {}
        const nameElement = generateProductTitle(name)
        const pointCostElement = generateProductPointCost(costText)
        const imageElement = generateProductImage(backgroundImageUrl, name || 'Reward product')
        const redeemButtonElement = generateRedeemButton(appliesToId)
        const descriptionOpener = generateDetailsOpener(i)
        const descriptionCloser = generateDetailsCloser(i)
        const descriptionBody = generateDetailsBody(description, i)
        const loginButtonElement = generateLoginButton()
        const productCardElelment = generateProductCardElement()
        const productCardInnerWrapper = generateProductCardInnerWrapper()
        productCardInnerWrapper.appendChild(imageElement)
        productCardInnerWrapper.appendChild(nameElement)
        productCardInnerWrapper.appendChild(descriptionOpener)
        productCardInnerWrapper.appendChild(descriptionBody)
        productCardInnerWrapper.appendChild(descriptionCloser)
        productCardInnerWrapper.appendChild(pointCostElement)
        if (loggedInCheck()) {
            productCardInnerWrapper.appendChild(redeemButtonElement)
        } else {
            productCardInnerWrapper.appendChild(loginButtonElement)
        }
        productCardElelment.appendChild(productCardInnerWrapper)
        productCardElelment.dataset.cardGroup = group
        
        this.inject('[data-card-group-container]', productCardElelment)
        this.productCards.push(productCardElelment)
    }

    renderProductCards() {
        const container = this.query('[data-card-group-container]')
        
        // Clear existing product cards if any
        if (container) {
            container.innerHTML = ''
        } else {
            console.error('[RewardsRedeem] Container [data-card-group-container] not found!');
        }
        this.productCards = []
        
        var i = 0;
        Object.keys(this.redemptionProductsSorted || {}).forEach((key) => {
            const pointProductsArray = this.redemptionProductsSorted[key]
            pointProductsArray.forEach(pointProduct => {
                i = i + 1;
                this.renderProductCard(pointProduct, key, i);
            })
        })
        this.filterDisplayByGroup('twoHundred')
    }

    initSlider() {
        // Only initialize if we're in carousel mode (not grid mode for 5000 points)
        const sliderElement = this.query('[data-rewards-redeem-slider]')
        if (window.innerWidth <= 750 && !this.swiperInstance && sliderElement && sliderElement.classList.contains('swiper') && typeof window.Swiper === 'function') {
            this.swiperInstance = new window.Swiper(sliderElement, sliderConfig);
        }
    }

    destroySlider() {
        if (this.swiperInstance && this.swiperInstance.destroy) {
            this.swiperInstance.destroy(true, true);
            this.swiperInstance = null;
        }
    }

    handleResize() {
        if (window.innerWidth > 750) {
            this.destroySlider();
        } else {
            this.initSlider();
        }
    }

    swellVariablesInit(activeOptions) {
        this.activeRedemptionOptions = activeOptions
        
        this.redemptionProducts = this.activeRedemptionOptions.filter(redemptionOption => {
            return redemptionOption.discountType === 'product'
        })
        
        this.twoHundredPointRedemptionPoints = this.redemptionProducts.filter(redemptionProduct => {
            return redemptionProduct.costInPoints === 200
        })
        this.fiveHundredPointRedemptionPoints = this.redemptionProducts.filter(redemptionProduct => {
            return redemptionProduct.costInPoints === 500
        })
        this.oneThousandPointRedemptionPoints = this.redemptionProducts.filter(redemptionProduct => {
            return redemptionProduct.costInPoints === 1000
        })
        this.fiveThousandPointRedemptionPoints = this.redemptionProducts.filter(redemptionProduct => {
            return redemptionProduct.costInPoints === 5000
        })
        
        this.redemptionProductsSorted = {
            twoHundred: this.twoHundredPointRedemptionPoints,
            fiveHundred: this.fiveHundredPointRedemptionPoints,
            oneThousand: this.oneThousandPointRedemptionPoints,
            fiveThousand: this.fiveThousandPointRedemptionPoints
        }
        
    }

    handleSwellSetup() {
        this.initializeFromSwell()
    }

    initializeFromSwell() {
        if (this._initialized || this._initializing) return this._initialized

        const swell = window.swellAPI
        if (!swell || typeof swell.getActiveRedemptionOptions !== 'function') return false

        let activeOptions
        try {
            activeOptions = swell.getActiveRedemptionOptions()
        } catch (error) {
            console.warn('[RewardsRedeem] Swell options are not ready:', error)
            return false
        }

        // Do not latch readiness before the SDK has supplied real options. A
        // later swell:setup event must still be able to complete initialization.
        if (!Array.isArray(activeOptions) || activeOptions.length === 0) return false

        this._initializing = true
        try {
            this.swellVariablesInit(activeOptions)
            this.renderProductCards()
            this.handleResize()
            this.initTabButtons()
            this.initProductCards()
            this.initRedeemSucess()
            this._initialized = true
            return true
        } catch (error) {
            console.error('[RewardsRedeem] Error during swell initialization:', error)
            return false
        } finally {
            this._initializing = false
        }
    }

    initListeners() {
        if (this._listenersAttached) return
        this._listenersAttached = true
        window.addEventListener('resize', this._onResize)
        document.addEventListener('swell:setup', this._onSwellSetup)

        // Catch up locally if the one-shot SDK event fired before this module
        // bound its listener. Do not redispatch globally and re-run other apps.
        this.initializeFromSwell()
        if (!this._initialized) {
            this._existingApiTimer = setTimeout(() => {
                this._existingApiTimer = null
                this.initializeFromSwell()
            }, 1000)
        }
    }

    disconnectedCallback() {
        window.removeEventListener('resize', this._onResize)
        document.removeEventListener('swell:setup', this._onSwellSetup)
        this._listenersAttached = false

        if (this._existingApiTimer) clearTimeout(this._existingApiTimer)
        if (this._gridInitTimer) clearTimeout(this._gridInitTimer)
        if (this._restoreInitTimer) clearTimeout(this._restoreInitTimer)
        this._existingApiTimer = null
        this._gridInitTimer = null
        this._restoreInitTimer = null

        this.destroySlider()
        if (this.tabsInstance?.destroy) this.tabsInstance.destroy()
        this.tabsInstance = null
        if (window.onBeforeProductRedemption === this._onBeforeProductRedemption) {
            window.onBeforeProductRedemption = null
        }
        this._onBeforeProductRedemption = null
        this._initialized = false
    }

    connectedCallback() {
        // Reattach listeners and rebuild state when Shopify moves the section
        // in the theme editor or during a section replacement.
        this.initListeners()
    }
}

customElements.define('rewards-redeem', RewardsRedeem)
